import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';
import { breadcrumbsIntegration, init as sentryReactInit } from '@sentry/react';
import { Deploy } from 'cordova-plugin-ionic';

import {
	collectBreadcrumb,
	createSentryBeforeSend,
	getClickBreadcrumbTestId,
	isIonicDeploySyncNetworkError,
	restoreBreadcrumbs,
} from '../internal/integrations/sentry';

export interface ISentryInitOptions {
	dsn: string;
	environment: string;

	/** Control which URLs should have trace propagation enabled */
	tracePropagationTargets?: (string | RegExp)[];

	/** 'driver' | 'passenger' — must match the `appName` passed to getSentryRspackPlugins() in the app's rspack.config.js, since both build the same release name independently */
	appName?: string;
}

const resolveReleaseInfo = async (
	appName: string | undefined,
	environment: string
) => {
	/**
	 * No native binary or live-update build exists for a plain web run, so fall back to
	 * the exact release name rspack.config.js baked in at build time — reading the same
	 * value CI used, rather than recomputing an independent formula that could drift.
	 */
	let release =
		process.env['NX_WEB_BUILD_RELEASE'] ??
		(appName
			? `solutions.mopla.apps.${appName}.web@${environment}`
			: undefined);
	let dist = process.env['NX_WEB_BUILD_DIST'] ?? environment;

	if (Capacitor.isNativePlatform()) {
		try {
			const [appInfo, liveUpdateVersion] = await Promise.all([
				App.getInfo(),
				Deploy.getCurrentVersion(),
			]);
			/** Native plugin bridges don't runtime-enforce their `string`-typed declarations — buildId/build can come back as a number and Sentry's ingest silently drops non-string `dist` values */
			dist = String(liveUpdateVersion?.buildId ?? appInfo.build);
			/** `+dist` is additive metadata on the release's semver build component — it doesn't replace `dist` above, which is what actually drives source map matching */
			release = `${appInfo.id}@${appInfo.version}+${dist}`;
		} catch (e) {
			console.error('Failed to resolve Sentry release/dist info', e);
		}
	}

	return { release, dist };
};

export const initSentry = async (
	options: ISentryInitOptions
): Promise<void> => {
	try {
		await initSentryUnsafe(options);
	} catch (e) {
		/** A monitoring SDK failing to initialize must never block app bootstrap */
		console.error('Failed to initialize Sentry', e);
	}
};

const initSentryUnsafe = async (options: ISentryInitOptions): Promise<void> => {
	const { dsn, environment, tracePropagationTargets, appName } = options;
	const { release, dist } = await resolveReleaseInfo(appName, environment);
	const isProduction = environment === 'production';

	Sentry.init(
		{
			dsn,
			environment,
			release,
			dist,
			integrations: [
				/** Sentry v9 BrowserTracing integration - provides automatic page load and navigation tracking */
				Sentry.browserTracingIntegration({
					/** Parameterize transaction names for better grouping */
					beforeStartSpan: (context) => ({
						...context,
						name: context.name
							.replace(/\/\d+/g, '/<id>') /** Replace numeric IDs */
							.replace(/\/[a-f0-9-]{36}/g, '/<uuid>') /** Replace UUIDs */,
					}),
					/** Filter out health checks and other noise */
					shouldCreateSpanForRequest: (url) =>
						!url.includes('/health') &&
						!url.includes('/ping') &&
						!url.includes('/metrics') &&
						!url.includes('/files/') && // Exclude file upload endpoints
						!url.includes('/upload'),
				}),
				breadcrumbsIntegration({
					console: false,
					dom: true,
					fetch: true,
					history: true,
					xhr: true,
				}),
			],
			tracePropagationTargets,
			tracesSampler: (samplingContext) => {
				if (samplingContext['isPayment']) {
					return 1;
				}

				if (
					samplingContext.attributes &&
					samplingContext.attributes['sentry.origin']?.includes('navigation')
				) {
					return isProduction ? 0.1 : 1;
				}

				return isProduction ? 0.2 : 1;
			},
			beforeSend: createSentryBeforeSend({
				filteredStatusCodes: [401],
				customFilter: (event, hint) => {
					let error =
						hint.originalException instanceof Error
							? hint.originalException
							: null;

					/** Special case for non-error objects */
					if (typeof hint.originalException === 'string') {
						error = new Error(hint.originalException);
					}

					/** Filter network errors */
					const isNetworkError = error?.message === 'Network Error';
					if (isNetworkError) return false;

					/** Filter vapid key errors on web */
					const isVapidKeyError = error?.message.includes('vapidKey');
					if (Capacitor.getPlatform() === 'web' && isVapidKeyError) {
						return false;
					}

					/** Filter expected Firebase auth errors */
					const isFirebaseAuthError =
						error?.message &&
						(error.message.includes('auth/invalid-credential') ||
							error.message.includes('auth/network-request-failed'));

					/** Sample rate: 30% */
					if (isFirebaseAuthError) {
						return Math.random() <= 0.3;
					}

					/**
					 * Filter cordova-plugin-ionic timeout errors
					 * @see node_modules/cordova-plugin-ionic/dist/common.js:241
					 */
					const isIonicPluginTimeout = error?.message.includes(
						'Request timed out. The device maybe offline.'
					);
					if (isIonicPluginTimeout) return false;

					if (isIonicDeploySyncNetworkError(event, error)) return false;

					return true; /** Allow event to be sent */
				},
			}),
			beforeBreadcrumb(breadcrumb, hint) {
				/** Filter out noisy network requests from breadcrumbs */
				if (breadcrumb.category === 'fetch' || breadcrumb.category === 'xhr') {
					const filteredUrlPatterns = ['/locales/'];
					const url = breadcrumb.data?.['url'];
					if (
						url &&
						filteredUrlPatterns.some((pattern: string) => url.includes(pattern))
					) {
						return null;
					}
				}

				/** Filter out noisy console messages from breadcrumbs */
				if (breadcrumb.category === 'console') {
					const filteredArgumentPatterns = [
						'RxDB dev-mode warning',
						'webpack-dev-server',
					];
					const message = breadcrumb.message;
					if (
						message &&
						filteredArgumentPatterns.some((pattern: string) =>
							message.includes(pattern)
						)
					) {
						return null;
					}
				}

				if (breadcrumb.category === 'ui.click' && hint && 'event' in hint) {
					const testId = getClickBreadcrumbTestId(hint['event']?.target);

					if (testId) {
						breadcrumb.message = testId;
					}
				}

				return collectBreadcrumb(breadcrumb);
			},
		},
		sentryReactInit
	);

	/** Restore breadcrumbs persisted before external redirect (e.g., TrueLayer bank auth) */
	restoreBreadcrumbs();
};
